import type { HouseMemberWithUser } from "@apps/dashboard/lib/home-expense-types";

/** Reparto equitativo en basis points (suma 10000). */
export function equalSplitsForUserIds(
  userIds: string[],
): { userId: string; shareBps: number }[] {
  const n = userIds.length;
  if (n === 0) return [];
  const base = Math.floor(10000 / n);
  const rem = 10000 - base * n;
  return userIds.map((userId, i) => ({
    userId,
    shareBps: base + (i < rem ? 1 : 0),
  }));
}

/** Todos los miembros tienen `defaultSplitBps` y suman 10000. */
export function membersUseConfiguredSplitBps(members: HouseMemberWithUser[]): boolean {
  if (members.length === 0) return false;
  const sum = members.reduce((a, m) => a + (m.defaultSplitBps ?? 0), 0);
  return members.every((m) => m.defaultSplitBps != null && m.defaultSplitBps >= 0) && sum === 10000;
}

/**
 * Reparto para crear el gasto: % del hogar si están configurados y suman 10000;
 * si no, partes iguales (misma lógica que la vista previa).
 */
export function expenseSplitsFromMembers(
  members: HouseMemberWithUser[],
): { userId: string; shareBps: number }[] {
  const n = members.length;
  if (n === 0) return [];
  if (membersUseConfiguredSplitBps(members)) {
    return members.map((m) => ({
      userId: m.userId,
      shareBps: m.defaultSplitBps ?? 0,
    }));
  }
  return equalSplitsForUserIds(members.map((m) => m.userId));
}

export type SplitAmountRow = {
  userId: string;
  name: string;
  shareBps: number;
  amountMinor: number;
};

/** Reparte importe en céntimos según shareBps; el resto va al último para cerrar el total. */
/** Misma composición de reparto (mismos userId + shareBps por fila). */
export function splitsShareBpsEqual(
  a: { userId: string; shareBps: number }[],
  b: { userId: string; shareBps: number }[],
): boolean {
  if (a.length !== b.length) return false;
  const norm = (rows: typeof a) =>
    [...rows].sort((p, q) => p.userId.localeCompare(q.userId));
  const na = norm(a);
  const nb = norm(b);
  return na.every(
    (s, i) => s.userId === nb[i]!.userId && s.shareBps === nb[i]!.shareBps,
  );
}

export type SplitModeTone = "accent" | "muted";

/**
 * Si el gasto coincide con el reparto que se calcularía hoy desde miembros (% hogar o partes iguales),
 * devuelve etiqueta + tono de pastilla; si no, `undefined`.
 */
export function storedExpenseSplitModeDisplay(
  members: HouseMemberWithUser[],
  expenseSplits: { userId: string; shareBps: number }[],
  t: (key: string) => string,
): { label: string; tone: SplitModeTone } | undefined {
  if (expenseSplits.length === 0) return undefined;
  const template = expenseSplitsFromMembers(members);
  if (!splitsShareBpsEqual(expenseSplits, template)) return undefined;
  return membersUseConfiguredSplitBps(members)
    ? {
        label: t("dashboardUi.expenses.form.sharedSplitUsesHousePercent"),
        tone: "accent",
      }
    : {
        label: t("dashboardUi.expenses.form.sharedSplitEqualParts"),
        tone: "muted",
      };
}

export function splitMinorByShareBps(
  totalMinor: number,
  splits: { userId: string; shareBps: number }[],
  nameForUserId: (userId: string) => string,
): SplitAmountRow[] {
  if (splits.length === 0) return [];
  const rows: SplitAmountRow[] = splits.map((s) => ({
    userId: s.userId,
    name: nameForUserId(s.userId),
    shareBps: s.shareBps,
    amountMinor: Math.floor((totalMinor * s.shareBps) / 10000),
  }));
  const allocated = rows.reduce((a, r) => a + r.amountMinor, 0);
  const rem = totalMinor - allocated;
  if (rem !== 0 && rows.length > 0) {
    rows[rows.length - 1]!.amountMinor += rem;
  }
  return rows;
}
